<template>
  <div class="authenticationHistory">
    <Loading v-if="loading" />
    <Title :title="$t('authenticationHistory')" />
    <div class="authenticationHistory__header">
      <div class="authenticationHistory__header-search">
        <img
          class="dropdown-img"
          src="@/assets/icons/Search.svg"
          alt="Search"
        />
        <input
          v-model="search"
          class="input-search"
          :placeholder="$t('search')"
          @input="changeInput"
        />
        <Dropdown
          class-name="dropdown-search"
          :selected-option="getSearchBy.selectedOption"
          :options="getSearchBy.options"
          @select-option="changeSearchBy"
        />
      </div>
      <div class="authenticationHistory__header-chooseDate">
        <Button
          class="getCSV-button"
          variant="bordered"
          :title="$t('downloadPDF')"
          @click="getPDF"
        />
        <div class="chooseDate-date">
          <div class="chooseDate-date-inputs">
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.startTime"
                name="startTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.endTime"
                name="endTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
          </div>
          <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
        </div>
      </div>
    </div>
    <div v-if="isDateCorrect && authenticationHistoryList?.length">
      <Table
        :data="authenticationHistoryList"
        :columns="columns"
        selected-field="id"
        @get-ssn="getSsn"
      />
    </div>
    <div
      v-if="!isDateCorrect || !authenticationHistoryList?.length"
      class="authenticationHistory__noReserve"
    >
      <p>{{ $t('noData') }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import moment from 'moment';
import { format } from 'date-fns';

import Loading from '@/views/Loading.vue';

import { searchActions, columns, columnsDow } from './configs';
import { InputFileEvent, Option } from '@/types';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { Params } from '@/store/modules/users/types';
import { AuthenticationHistoryData } from '@/store/modules/callerAuthentication/types';

const transformationDate = new Date().setDate(new Date().getDate() - 3);

@Options({
  name: 'AuthenticationHistory',
  components: {
    Loading
  },
  data() {
    return {
      loading: false,
      search: '',
      getSearchBy: {
        selectedOption: {
          value: 'firstName',
          label: this.$t('firstName')
        },
        options: searchActions(this.$t)
      },
      dateRange: {
        startTime: format(new Date(transformationDate), 'dd/MM/yyyy'),
        endTime: format(new Date(), 'dd/MM/yyyy')
      },
      columnsDow: columnsDow(this.$t),
      columns: columns(this.$t),
      authenticationHistoryList: [],
      pharmacyId: '',
      showSSN: false,
      buttonLoaded: false
    };
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('callerAuthentication', [
      'authenticationHistory',
      'decodedSsn'
    ]),
    isDateCorrect() {
      if (this.isDatePresent) {
        const startTime = this.dateRange.startTime.split('/');
        const endTime = this.dateRange.endTime.split('/');

        const defaultParams = {
          ...this.dateRange,
          startTime: new Date(
            +startTime[2],
            startTime[1] - 1,
            +startTime[0]
          ).valueOf(),
          endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
        };

        return defaultParams.endTime >= defaultParams.startTime;
      }

      return true;
    },
    isDatePresent() {
      const startTime = this.dateRange.startTime.split('/');
      const endTime = this.dateRange.endTime.split('/');

      const defaultParams = {
        ...this.dateRange,
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
      };

      return defaultParams && defaultParams.startTime && defaultParams.endTime;
    }
  },
  created() {
    if (this.user) {
      this.pharmacyId = this.user?.pharmacyId;
      this.show();
    }
  },
  methods: {
    ...mapActions('callerAuthentication', [
      'getAuthenticationHistory',
      'getAuthenticationHistoryDecodedSsn'
    ]),
    show() {
      this.initRequest();
    },
    initRequest() {
      let startTime = this.dateRange.startTime.split('/');
      let endTime = this.dateRange.endTime.split('/');

      startTime = new Date(
        +startTime[2],
        startTime[1] - 1,
        +startTime[0]
      ).valueOf();

      const today = new Date().getDate();

      if (today === +endTime[0]) {
        endTime = new Date().valueOf();
      } else {
        endTime = new Date(
          +endTime[2],
          endTime[1] - 1,
          +endTime[0],
          23,
          59,
          59
        ).valueOf();
      }

      this.params = {
        startTime: startTime + new Date(startTime).getTimezoneOffset() * -60000,
        endTime: endTime + new Date(endTime).getTimezoneOffset() * -60000,
        pharmacyId: this.user.pharmacyId
      };

      this.getAuthenticationHistoryList(this.params);
    },
    async getAuthenticationHistoryList(params: Params) {
      if (
        params.pharmacyId &&
        params.startTime &&
        params.endTime &&
        this.isDateCorrect
      ) {
        this.loading = true;

        await this.getAuthenticationHistory(params);
        this.authenticationHistoryList = this.authenticationHistory;

        this.loading = false;
      }
    },
    async getSsn(id: string) {
      const params: Params = {
        pharmacyId: this.user?.pharmacyId,
        recordId: id
      };

      this.loading = true;

      await this.getAuthenticationHistoryDecodedSsn(params);

      const activeAuthenticationHistory = this.authenticationHistoryList.find(
        (i: AuthenticationHistoryData) => i.id === id
      );

      if (activeAuthenticationHistory) {
        activeAuthenticationHistory.ssn = this.decodedSsn.ssn;
      }

      this.loading = false;
    },
    changeDate(e: InputFileEvent) {
      const { name, value } = e.target;
      this.buttonLoaded = false;

      this.dateRange = {
        ...this.dateRange,
        [name]: value
      };

      this.initRequest();
      this.buttonLoaded = true;
    },
    closeTimePanel() {
      this.buttonLoaded = false;
    },
    changeInput(e: InputFileEvent) {
      const { value } = e.target;

      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        if (value === '') {
          this.authenticationHistoryList = this.authenticationHistory;
        } else if (this.getSearchBy.selectedOption.value === 'firstName') {
          this.authenticationHistoryList = [
            ...(
              Object.values(
                this.authenticationHistory
              ) as AuthenticationHistoryData[]
            ).flat()
          ].filter((item: AuthenticationHistoryData) =>
            item.firstName.toLowerCase().includes(value?.toLowerCase())
          );
        } else if (this.getSearchBy.selectedOption.value === 'lastName') {
          this.authenticationHistoryList = [
            ...(
              Object.values(
                this.authenticationHistory
              ) as AuthenticationHistoryData[]
            ).flat()
          ].filter((item: AuthenticationHistoryData) =>
            item.lastName.toLowerCase().includes(value?.toLowerCase())
          );
        } else if (this.getSearchBy.selectedOption.value === 'phoneNumber') {
          this.authenticationHistoryList = [
            ...(
              Object.values(
                this.authenticationHistory
              ) as AuthenticationHistoryData[]
            ).flat()
          ].filter((item: AuthenticationHistoryData) =>
            item.phoneNumber.toLowerCase().includes(value?.toLowerCase())
          );
        }
      }, 600);
    },
    changeSearchBy(option: readonly Option[]) {
      this.getSearchBy.selectedOption = option;

      this.authenticationHistoryList = [
        ...(
          Object.values(
            this.authenticationHistory
          ) as AuthenticationHistoryData[]
        ).flat()
      ].filter((item: AuthenticationHistoryData) =>
        (
          item[
            this.getSearchBy.selectedOption
              .value as keyof AuthenticationHistoryData
          ] as string
        )
          ?.toLowerCase()
          .includes(this.search.toLowerCase())
      );
    },
    dateTime(value: number) {
      if (value) {
        return moment(value).format('DD.MM.YYYY HH:mm');
      }
    },
    getPDF() {
      const data = this.authenticationHistoryList.map(
        (el: AuthenticationHistoryData) => ({
          userNAME: `${el.lastName} ${el.firstName}`,
          phoneNumber: el.phoneNumber,
          timestamp: this.dateTime(el.timestamp)
        })
      );

      const dataPDF = data.map((obj: AuthenticationHistoryData) =>
        Object.values(obj)
      );

      const columns = [...this.columnsDow].map((i: Option) => i.label);

      const doc = new jsPDF('landscape');
      autoTable(doc, {
        head: [columns],
        body: dataPDF,
        theme: 'grid',
        headStyles: { fillColor: [71, 152, 127] },
        bodyStyles: { fillColor: [255, 255, 255] },
        margin: { top: 10 }
      });

      doc.save(`${this.$t('callerAuthentication')}.pdf`);
    },
    toggleShow() {
      this.showSSN = !this.showSSN;
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.pharmacyId = this.user?.pharmacyId;
        this.show();
      }
    }
  }
})
export default class AuthenticationHistory extends Vue {}
</script>

<style lang="scss">
.authenticationHistory {
  display: flex;
  flex-direction: column;
  padding: 24px;
  .authenticationHistory__header {
    display: flex;
    justify-content: space-between;
    padding-top: 32px;
    padding-bottom: 10px;
    @media screen and (max-width: 1220px) {
      flex-direction: column;
    }
    .authenticationHistory__header-search {
      position: relative;
      @media screen and (max-width: 1350px) {
        margin-bottom: 24px;
      }
      .input-search {
        width: 245px;
        height: 38px;
        padding-left: 36px;
        font-size: $fourteenSize;
        border: 1px solid $border;
        border-right: 0;
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
        outline: none;
      }
      .dropdown-img {
        position: absolute;
        top: 14px;
        left: 17px;
      }
      .dropdown {
        max-width: 150px;
        .dropdown-search {
          height: 40px;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      }
    }
    .authenticationHistory__header-chooseDate {
      display: flex;
      justify-content: flex-start;
      @media screen and (max-width: 1350px) {
        justify-content: flex-end;
      }
      .chooseDate-date {
        height: 100%;
        margin-left: 10px;
        .chooseDate-date-inputs {
          display: flex;
          align-items: center;
          .chooseDate-button {
            width: 60px;
            margin-right: 10px;
            z-index: 101;
            cursor: pointer;
          }
          .input {
            margin-bottom: 0;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            .input-img {
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 0;
            }
          }
          .date-img {
            margin: 0 8px;
          }
        }
        .date-error {
          color: $red;
          font-size: $fourteenSize;
          margin-top: 10px;
        }
      }
    }
  }
}
</style>
